import { ENTRY_PAGE_SIZE_DEFAULT, type EntryMatrix, type EntryOrgSort, type EntryPhase, entryOrgSortDefaultDir } from "@seagames/contracts";
import { ListToolbar, Pagination, Select, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Suspense, lazy, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadEntriesCsv, entryByEventOptions, entryMatrixOptions, entryOrganizationsOptions } from "../../lib/api/entries";
import { errorMessage } from "../../lib/api/messages";
import { toast } from "../../lib/ui/toast";
import { DownloadIcon } from "../accreditation/icons";
import { NocFlag } from "../common/noc-flag";
import { StatusGuide } from "../common/status-guide";
import { PageBarButton, usePageBar } from "../shell/page-bar";
import { GenderTag } from "../shortlist/parts";
import { EmptyBlock, Section } from "../transport/parts";
import { InfoIcon } from "../transport/icons";
import { type CardSort, type EntriesSearch, type OverviewTab, cardSortsFor } from "./entries-page";
import { CrownGlyph as CrownMark, useStrengthAvailable } from "./history";
import { NocCards, NocCardsSkeleton } from "./noc-cards";
import { HowItWorksSheet, StatePill, useEntriesGuide } from "./parts";
import "./overview.css";

// Its own chunk: admins on the SEA Games only (the medal-history dataset is a further chunk behind it).
const StrengthDashboard = lazy(() => import("./strength").then((m) => ({ default: m.StrengthDashboard })));
const StrengthSkeleton = () => (
	<div className="grid gap-[0.875rem]" aria-busy>
		<div className="skeleton h-[11rem] rounded-[0.75rem]" />
		<div className="skeleton h-[22rem] rounded-[0.75rem]" />
	</div>
);

/**
 * One phase's NOC list (the EOI and EBN routes without `org`), for admins and
 * host groups. The statistics are their own page (`/accreditation/entries`,
 * admins: `analytics/dashboard.tsx`), so this page is about the NOCs:
 *
 *   progress  "N of M submitted · K responded" with a bar (the summary of
 *             `/entries/organizations`, over every NOC, not the page)
 *   cards     one per NOC, THIS phase's numbers only (`noc-cards.tsx`):
 *             flag, state, three numbers, window, Open → the NOC's editor on
 *             behalf (`?org=`); search and sort run in SQL (`sort`, `dir`)
 *   details   admins: Matrix (NOCs × sports, this phase's numbers; a sport's
 *             header picks `?sport=` for the by-event table) and the
 *             Strength map (SEA Games only), as tabs (URL `tab`)
 *
 * Reads: `GET /entries/organizations` (ONE aggregate, offset paged),
 * `/entries/matrix` (same page of NOCs), `/entries/by-event`.
 */
export function EntriesOverview({
	phase,
	gamesId,
	search,
	patch,
	admin,
}: {
	phase: EntryPhase;
	gamesId: string;
	search: EntriesSearch;
	patch: (next: Partial<EntriesSearch>) => void;
	admin: boolean;
}) {
	const { t } = useTranslation("entries");
	const { t: ts } = useTranslation("entriesStats");
	const { formatNumber: fmt } = useUiStrings();
	const [help, setHelp] = useState(false);
	const [csvBusy, setCsvBusy] = useState(false);
	const page = search.page ?? 1;
	const pageSize = search.page_size ?? ENTRY_PAGE_SIZE_DEFAULT;
	const sorts = cardSortsFor(phase);
	// A sort the other phase offers (a link carried over) falls back to the name.
	const sort: CardSort = search.sort && sorts.includes(search.sort) ? search.sort : "name";
	const apiSort: EntryOrgSort = sort === "state" ? (phase === "EOI" ? "eoi_state" : "ebn_state") : sort;
	const dir = search.dir ?? entryOrgSortDefaultDir(apiSort);
	const orgs = useQuery(
		entryOrganizationsOptions({
			games_id: gamesId,
			page,
			page_size: pageSize,
			search: search.search,
			// Only when not the default, so the default list shares its cache (the dashboard's roster, the Strength map).
			...(sort !== "name" ? { sort: apiSort } : {}),
			...(search.dir ? { dir: search.dir } : {}),
		}),
	);
	const guide = useEntriesGuide();
	const strengthOk = useStrengthAvailable(gamesId) && admin;

	const csv = async () => {
		setCsvBusy(true);
		try {
			await downloadEntriesCsv(gamesId, phase, t("page.csvName", { phase: phase.toLowerCase() }));
		} catch (err) {
			toast.error(err);
		} finally {
			setCsvBusy(false);
		}
	};

	usePageBar({
		subtitle: t("overview.subtitle"),
		actions: (
			<>
				{admin && <PageBarButton icon={<DownloadIcon />} label={t("page.csv")} onClick={() => void csv()} disabled={csvBusy} />}
				<PageBarButton icon={<InfoIcon />} label={t("help.button")} onClick={() => setHelp(true)} />
			</>
		),
	});

	const s = orgs.data?.summary;
	const final = s ? (phase === "EOI" ? s.eoi_final : s.ebn_final) : 0;
	const started = s ? (phase === "EOI" ? s.eoi_started : s.ebn_started) : 0;
	const filtered = !!search.search;
	const flip = dir === "asc" ? "desc" : "asc";

	const nocs = (
		<section aria-labelledby="ent-nocs-h" className="flex min-w-0 flex-col gap-[0.875rem]">
			<div className="ent-ov__top">
				<h2 id="ent-nocs-h" className="ent-ov__h">
					{t("overview.nocs")}
					{orgs.data && <small>{t("overview.count", { count: orgs.data.total, n: fmt(orgs.data.total) })}</small>}
				</h2>
				{s && (
					<div className="ent-ov__progress">
						<span>{t("overview.progress", { final: fmt(final), total: fmt(s.organizations), started: fmt(started) })}</span>
						<span className="ent-ov__bar" role="img" aria-label={t("overview.progress", { final: fmt(final), total: fmt(s.organizations), started: fmt(started) })}>
							<i data-s="started" style={{ inlineSize: `${s.organizations > 0 ? (started / s.organizations) * 100 : 0}%` }} />
							<i data-s="final" style={{ inlineSize: `${s.organizations > 0 ? (final / s.organizations) * 100 : 0}%` }} />
						</span>
					</div>
				)}
			</div>
			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => patch({ search: v || undefined, page: undefined })}
				searchPlaceholder={t("overview.search")}
				sort={{
					value: sort,
					options: sorts.map((k) => ({ value: k, label: t(`overview.cards.sort.${k}`) })),
					onChange: (v) => patch({ sort: v === "name" ? undefined : (v as CardSort), dir: undefined, page: undefined }),
				}}
				end={
					<>
						<button
							type="button"
							className="btn btn-sm btn-line min-h-[2.5rem] gap-[0.375rem]"
							title={t(`overview.cards.dir.${flip}`)}
							aria-label={t(`overview.cards.dir.${flip}`)}
							onClick={() => patch({ dir: flip === entryOrgSortDefaultDir(apiSort) ? undefined : flip, page: undefined })}
						>
							<svg viewBox="0 0 16 16" aria-hidden="true" className={`size-[1rem] transition-transform ${dir === "asc" ? "" : "rotate-180"}`}>
								<path d="M8 13V3m-4 4 4-4 4 4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
							{/* The word only where the toolbar has room; the arrow (and the button's name) say it everywhere. */}
							<span className="hidden min-[90rem]:inline">{t(`overview.cards.dir.short.${dir}`)}</span>
						</button>
						<StatusGuide title={guide.title} groups={guide.groups} />
					</>
				}
				onClearAll={() => patch({ search: undefined, page: undefined })}
			/>
			{orgs.isError && !orgs.data ? (
				<EmptyBlock
					title={t("common.loadFailed")}
					hint={errorMessage(orgs.error)}
					action={
						<button type="button" className="btn btn-sm btn-line" onClick={() => void orgs.refetch()}>
							{t("common.tryAgain")}
						</button>
					}
				/>
			) : !orgs.data ? (
				<NocCardsSkeleton />
			) : orgs.data.items.length === 0 ? (
				filtered ? (
					<EmptyBlock
						title={t("overview.empty.filteredTitle")}
						hint={t("overview.empty.filteredHint")}
						action={
							<button type="button" className="btn btn-sm btn-outline" onClick={() => patch({ search: undefined })}>
								{t("overview.empty.clear")}
							</button>
						}
					/>
				) : (
					<EmptyBlock title={t("overview.empty.noneTitle")} hint={t("overview.empty.noneHint")} />
				)
			) : (
				<NocCards rows={orgs.data.items} phase={phase} gamesId={search.games_id} loading={orgs.isFetching} />
			)}
			{orgs.data && orgs.data.total > pageSize && (
				<Pagination
					page={page}
					pageSize={pageSize}
					total={orgs.data.total}
					labels={{
						summary: ({ from, to, total }) => t("overview.pagination.summary", { from: fmt(from), to: fmt(to), total: fmt(total) }),
						empty: t("overview.pagination.empty"),
					}}
					onPageChange={(p) => patch({ page: p === 1 ? undefined : p })}
					onPageSizeChange={(n) => patch({ page_size: n === ENTRY_PAGE_SIZE_DEFAULT ? undefined : n, page: undefined })}
				/>
			)}
		</section>
	);

	// A host group: the NOCs its scope covers — no matrix, by-event, Strength map or export (admin-only reads).
	if (!admin)
		return (
			<div className="ent-ov flex min-w-0 flex-col gap-[1.25rem]">
				{nocs}
				<HowItWorksSheet open={help} onClose={() => setHelp(false)} />
			</div>
		);

	// Admins: the cards first, then the detail views as tabs.
	const tabs: OverviewTab[] = strengthOk ? ["matrix", "strength"] : ["matrix"];
	const tab: OverviewTab = search.tab && tabs.includes(search.tab) ? search.tab : search.view === "strength" && strengthOk ? "strength" : "matrix";
	const setTab = (next: OverviewTab) => patch({ tab: next === "matrix" ? undefined : next, view: undefined });
	const tabClass = (on: boolean) =>
		`inline-flex min-h-[2.25rem] items-center gap-[0.375rem] rounded-[0.375rem] px-[0.875rem] text-[0.8125rem] no-underline focus-visible:outline-2 focus-visible:outline-gms-navy ${
			on ? "bg-base-100 font-semibold text-gms-navy shadow-[0_1px_2px_rgba(18,20,63,0.12),0_0_0_1px_var(--color-gms-line)]" : "font-medium text-gms-ink-2 hover:text-gms-navy-ink"
		}`;
	return (
		<div className="ent-ov flex min-w-0 flex-col gap-[1.75rem]">
			{nocs}

			<div className="flex min-w-0 flex-col gap-[0.75rem]">
				<div className="flex flex-wrap items-center justify-between gap-[0.5rem]">
					<h2 className="ent-ov__h">{ts("tabs.title")}</h2>
					{tabs.length > 1 && (
						<div role="tablist" aria-label={ts("tabs.label")} className="grid w-fit grid-flow-col gap-[0.25rem] rounded-field bg-gms-soft-2 p-[0.25rem]">
							{tabs.map((k) => (
								<button
									key={k}
									type="button"
									role="tab"
									id={`ent-tab-${k}`}
									aria-selected={tab === k}
									aria-controls="ent-tabpanel"
									className={tabClass(tab === k)}
									onClick={() => setTab(k)}
								>
									{k === "strength" && (
										<span className="ent-tier" data-tier="TOP" aria-hidden>
											<CrownMark />
										</span>
									)}
									{ts(`tabs.${k}`)}
								</button>
							))}
						</div>
					)}
				</div>
				<div role="tabpanel" id="ent-tabpanel" aria-labelledby={`ent-tab-${tab}`} className="flex min-w-0 flex-col gap-[1.125rem]">
					{tab === "strength" ? (
						<Suspense fallback={<StrengthSkeleton />}>
							<StrengthDashboard phase={phase} gamesId={gamesId} search={search} patch={patch} />
						</Suspense>
					) : (
						<>
							<MatrixSection
								phase={phase}
								gamesId={gamesId}
								page={page}
								pageSize={pageSize}
								sport={search.sport}
								onSport={(v) => {
									patch({ sport: v });
									// The events open below the matrix: bring them into view (EBN only; EOI has no by-event table).
									if (v) requestAnimationFrame(() => document.getElementById("ent-byevent-h")?.scrollIntoView({ behavior: "smooth", block: "start" }));
								}}
							/>
							{phase === "EBN" && <ByEventSection gamesId={gamesId} sport={search.sport} onSport={(v) => patch({ sport: v })} />}
						</>
					)}
				</div>
			</div>

			<HowItWorksSheet open={help} onClose={() => setHelp(false)} />
		</div>
	);
}

/** EOI: estimated athletes (men + women; a number not given counts 0); EBN: Σ entries. */
const cellValue = (phase: EntryPhase, c: EntryMatrix["items"][number]["cells"][number]) =>
	phase === "EOI" ? (c.est_athletes_men ?? 0) + (c.est_athletes_women ?? 0) : c.ebn_entries;
/** An estimate a NOC did not give (null) reads "—", never 0. */
const given = (fmt: (n: number) => string, v: number | null) => (v === null ? "—" : fmt(v));

function MatrixSection({
	phase,
	gamesId,
	page,
	pageSize,
	sport,
	onSport,
}: {
	phase: EntryPhase;
	gamesId: string;
	page: number;
	pageSize: number;
	sport: string | undefined;
	onSport: (v: string | undefined) => void;
}) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const matrix = useQuery(entryMatrixOptions({ games_id: gamesId, phase, page, page_size: pageSize }));
	// A sport's head opens its by-event table, which only the EBN page has.
	const drill = phase === "EBN";
	const m = matrix.data;
	const max = m ? Math.max(1, ...m.items.flatMap((r) => r.cells.map((c) => cellValue(phase, c)))) : 1;
	const colTotal = (sportId: string) =>
		(m?.items ?? []).reduce((n, r) => {
			const c = r.cells.find((x) => x.sport_id === sportId);
			return n + (c ? cellValue(phase, c) : 0);
		}, 0);
	return (
		<Section
			id="ent-matrix-h"
			title={t("overview.matrix.title")}
			end={<span className="text-[0.75rem] text-gms-ink-3">{t(`overview.matrix.metric.${phase}`)}</span>}
		>
			{matrix.isError ? (
				<EmptyBlock
					title={t("common.loadFailed")}
					hint={errorMessage(matrix.error)}
					action={
						<button type="button" className="btn btn-sm btn-line" onClick={() => void matrix.refetch()}>
							{t("common.tryAgain")}
						</button>
					}
				/>
			) : !m ? (
				<div className="skeleton m-[0.75rem] h-[8rem] rounded-[0.5rem]" />
			) : m.sports.length === 0 || m.items.length === 0 ? (
				<EmptyBlock title={t("overview.matrix.empty")} />
			) : (
				<>
					<div className={`ent-matrix__scroll ${matrix.isFetching ? "opacity-70 transition-opacity" : "transition-opacity"}`}>
						<table className="ent-matrix" aria-label={t("overview.matrix.label")}>
							<thead>
								<tr>
									<th scope="col">{t("overview.matrix.noc")}</th>
									{m.sports.map((s) => {
										const head = (
											<>
												<span className="ent-matrix__code">{s.code}</span>
												<span className="ent-matrix__sport">{s.name}</span>
											</>
										);
										return (
											<th key={s.sport_id} scope="col" title={s.name}>
												{/* Only EBN has a by-event table to open; on EOI the head is a plain label. */}
												{drill ? (
													<button
														type="button"
														className="ent-matrix__drill"
														aria-label={t("overview.matrix.drill", { sport: s.name })}
														aria-pressed={sport === s.sport_id}
														onClick={() => onSport(sport === s.sport_id ? undefined : s.sport_id)}
													>
														{head}
													</button>
												) : (
													<span className="ent-matrix__head">{head}</span>
												)}
											</th>
										);
									})}
									<th scope="col" data-total>
										{t("overview.matrix.total")}
									</th>
								</tr>
							</thead>
							<tbody>
								{m.items.map((r) => {
									const total = r.cells.reduce((n, c) => n + cellValue(phase, c), 0);
									const any = r.cells.length > 0;
									return (
										<tr key={r.organization.id} data-quiet={!any || undefined}>
											<th scope="row" title={r.organization.name}>
												<span className="ent-matrix__rowhead">
													<span className="ent-matrix__noc">
														<NocFlag code={r.organization.code} size="sm" />
														{r.organization.code ?? r.organization.name}
													</span>
													<StatePill state={r.state} />
												</span>
											</th>
											{m.sports.map((s) => {
												const c = r.cells.find((x) => x.sport_id === s.sport_id);
												if (!c)
													return (
														<td key={s.sport_id}>
															<span className="ent-heat" data-empty title={t("overview.matrix.legend.empty")}>
																·
															</span>
														</td>
													);
												const v = cellValue(phase, c);
												const heat = v / max;
												const label =
													phase === "EOI"
														? t("overview.matrix.cellEoi", { org: r.organization.name, sport: s.name, m: given(fmt, c.est_athletes_men), w: given(fmt, c.est_athletes_women), o: given(fmt, c.est_officials) })
														: t("overview.matrix.cellEbn", { org: r.organization.name, sport: s.name, events: fmt(c.ebn_events), entries: fmt(c.ebn_entries) });
												return (
													<td key={s.sport_id}>
														<span
															className="ent-heat"
															style={{ "--heat": heat.toFixed(3) } as React.CSSProperties}
															data-dark={heat > 0.55 ? "" : undefined}
															data-tick={v === 0 || undefined}
															title={label}
														>
															{v > 0 ? fmt(v) : "✓"}
														</span>
													</td>
												);
											})}
											<td data-total>{any && total > 0 ? fmt(total) : "—"}</td>
										</tr>
									);
								})}
							</tbody>
							<tfoot>
								<tr>
									<th scope="row">{t("overview.matrix.total")}</th>
									{m.sports.map((s) => {
										const v = colTotal(s.sport_id);
										return (
											<td key={s.sport_id} data-total>
												{v > 0 ? fmt(v) : "—"}
											</td>
										);
									})}
									<td data-total>{fmt(m.sports.reduce((n, s) => n + colTotal(s.sport_id), 0))}</td>
								</tr>
							</tfoot>
						</table>
					</div>
					{/* What the marks mean: never colour alone. */}
					<ul className="ent-matrix__legend" aria-label={t("overview.matrix.legendLabel")}>
						<li>
							<span className="ent-matrix__ramp" aria-hidden>
								<i style={{ "--heat": "0.15" } as React.CSSProperties} />
								<i style={{ "--heat": "0.5" } as React.CSSProperties} />
								<i style={{ "--heat": "0.9" } as React.CSSProperties} />
							</span>
							{t(`overview.matrix.legend.${phase}`)}
						</li>
						{phase === "EOI" && (
							<li>
								<span className="ent-heat ent-matrix__key" data-tick aria-hidden>
									✓
								</span>
								{t("overview.matrix.legend.tick")}
							</li>
						)}
						<li>
							<span className="ent-heat ent-matrix__key" data-empty aria-hidden>
								·
							</span>
							{t("overview.matrix.legend.empty")}
						</li>
						{drill && <li className="ent-matrix__hint">{t("overview.matrix.legend.drill")}</li>}
					</ul>
				</>
			)}
		</Section>
	);
}

function ByEventSection({ gamesId, sport, onSport }: { gamesId: string; sport: string | undefined; onSport: (v: string | undefined) => void }) {
	const { t } = useTranslation("entries");
	const { t: ts } = useTranslation("shortlist");
	const { formatNumber: fmt } = useUiStrings();
	const data = useQuery(entryByEventOptions(gamesId, sport));
	// The picker lists the sports any NOC chose (the EOI matrix's columns).
	const sports = useQuery(entryMatrixOptions({ games_id: gamesId, phase: "EOI", page: 1, page_size: 100 }));
	const b = data.data;
	return (
		<Section
			id="ent-byevent-h"
			title={t("overview.byEvent.title")}
			end={
				<Select
					size="sm"
					aria-label={t("overview.byEvent.sport")}
					placeholder={t("overview.byEvent.pickSport")}
					value={sport ?? null}
					clearable
					onChange={(v) => onSport(v ?? undefined)}
					options={(sports.data?.sports ?? []).map((s) => ({ value: s.sport_id, label: `${s.name} (${s.code})` }))}
					className="min-w-[14rem]"
				/>
			}
		>
			{!sport ? (
				<EmptyBlock title={t("overview.byEvent.pickSport")} hint={t("overview.byEvent.pickHint")} />
			) : data.isError ? (
				<EmptyBlock
					title={t("common.loadFailed")}
					hint={errorMessage(data.error)}
					action={
						<button type="button" className="btn btn-sm btn-line" onClick={() => void data.refetch()}>
							{t("common.tryAgain")}
						</button>
					}
				/>
			) : !b ? (
				<div className="skeleton m-[0.75rem] h-[8rem] rounded-[0.5rem]" />
			) : b.events.length === 0 || b.organizations.length === 0 ? (
				<EmptyBlock title={t("overview.byEvent.empty")} />
			) : (
				<div className="overflow-x-auto">
					<table className="ent-table min-w-[40rem]">
						<thead>
							<tr>
								<th scope="col">{t("overview.byEvent.event")}</th>
								{b.organizations.map((o) => (
									<th key={o.id} scope="col" data-num title={o.name}>
										<span className="inline-flex items-center gap-[0.375rem]">
											<NocFlag code={o.code} size="xs" />
											{o.code ?? o.name}
										</span>
									</th>
								))}
								<th scope="col" data-num>
									{t("overview.byEvent.total")}
								</th>
							</tr>
						</thead>
						<tbody>
							{b.events.map((e) => (
								<tr key={e.event_id}>
									<td className="min-w-[14rem]">
										<span className="flex flex-wrap items-center gap-[0.375rem]">
											<b className="font-semibold text-gms-navy-ink">{e.name}</b>
											<GenderTag gender={e.gender} />
											<span className="ent-chip">{ts(`competitorType.${e.competitor_type}`)}</span>
										</span>
										<span className="text-[0.71875rem] text-gms-ink-3">
											{e.discipline.name}
											{e.max_entries_per_noc !== null && <> · {t("overview.byEvent.cap", { n: fmt(e.max_entries_per_noc) })}</>}
										</span>
									</td>
									{b.organizations.map((o) => {
										const v = e.entries.find((x) => x.organization_id === o.id)?.entries;
										return (
											<td key={o.id} data-num className={v ? "font-semibold text-gms-navy-ink" : "text-gms-ink-3"}>
												{v ? fmt(v) : "—"}
											</td>
										);
									})}
									<td data-num className="font-bold text-gms-navy-ink">
										{fmt(e.total_entries)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</Section>
	);
}
